import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useOutletContext } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { cn } from '../lib/utils';
import { BRAND_GRADIENT, BRAND_GRADIENT_TEXT } from '../lib/brand';
import { Reveal } from '../components/landing/Reveal';

/**
 * The Career Catalyst landing page — graduated from the approved
 * /landing-draft prototype (owner sign-off 2026-06-10).
 * Design language: docs/frontend/design-system.md + this file as exemplar.
 * Copy rules: no em dashes, no competitor mentions, outcomes only.
 */

const CTA_PRIMARY = cn(
  'cursor-pointer rounded-lg px-7 py-3.5 text-center text-base font-semibold text-white',
  'shadow-lg shadow-primary/30 transition-all hover:opacity-95 hover:shadow-primary/50',
  'focus-visible:ring-3 focus-visible:ring-ring/50 outline-hidden active:translate-y-px',
  BRAND_GRADIENT,
);

const CTA_SECONDARY = cn(
  'cursor-pointer rounded-lg border border-border bg-background/40 px-7 py-3.5 text-center',
  'text-base font-medium text-foreground/85 backdrop-blur-xs transition-colors',
  'hover:border-foreground/25 hover:text-foreground',
  'focus-visible:ring-3 focus-visible:ring-ring/50 outline-hidden',
);

/* ---------- Count-up that starts when visible ---------- */

function useCountUp(target: number, durationMs = 1400) {
  const ref = useRef<HTMLElement | null>(null);
  const [value, setValue] = useState(0);
  const started = useRef(false);

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setValue(target);
      return;
    }
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting || started.current) return;
        started.current = true;
        const t0 = performance.now();
        const tick = (t: number) => {
          const p = Math.min((t - t0) / durationMs, 1);
          setValue(Math.round(target * (1 - Math.pow(1 - p, 3))));
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
        observer.disconnect();
      },
      { threshold: 0.4 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [target, durationMs]);

  return { ref, value };
}

/* ---------- Device: the reviewed job posting ---------- */

const REVIEW_LINES: { text: string; verdict: 'matched' | 'partial' | 'missing' }[] = [
  { text: '5+ years building production software', verdict: 'matched' },
  { text: 'Leads cross-functional delivery', verdict: 'matched' },
  { text: 'Cloud cost optimization at scale', verdict: 'partial' },
  { text: 'Kubernetes in production', verdict: 'missing' },
];

const VERDICT_STYLE = {
  matched: 'text-success',
  partial: 'text-warning',
  missing: 'text-destructive',
} as const;

const VERDICT_DOT = {
  matched: 'bg-success',
  partial: 'bg-warning',
  missing: 'bg-destructive',
} as const;

const VERDICT_LABEL = {
  matched: 'Matched',
  partial: 'Partial',
  missing: 'Missing',
} as const;

const ReviewedPosting: React.FC<{ matchValue: number; matchRef: React.Ref<HTMLElement> }> = ({
  matchValue,
  matchRef,
}) => (
  <div
    aria-hidden
    className="animate-rise w-full rounded-2xl border border-border bg-card/80 shadow-xl backdrop-blur-xs"
    style={{ '--rise-delay': '400ms' } as React.CSSProperties}
  >
    <div className="px-6 pt-5 sm:px-7">
      <p className="text-[11px] font-semibold tracking-[0.14em] text-muted-foreground uppercase">
        Senior Platform Engineer · job posting
      </p>
    </div>
    <div className="mt-3 divide-y divide-border border-t border-border px-6 sm:px-7">
      {REVIEW_LINES.map((line, i) => (
        <div
          key={line.text}
          className="animate-rise flex items-center justify-between gap-5 py-3"
          style={{ '--rise-delay': `${550 + i * 130}ms` } as React.CSSProperties}
        >
          <p className="text-[15px] text-foreground sm:text-base">{line.text}</p>
          <p
            className={cn(
              'flex shrink-0 items-center gap-1.5 text-[11px] font-semibold tracking-[0.14em] uppercase',
              VERDICT_STYLE[line.verdict],
            )}
          >
            <span className={cn('size-1.5 rounded-full', VERDICT_DOT[line.verdict])} />
            {VERDICT_LABEL[line.verdict]}
          </p>
        </div>
      ))}
    </div>
    <div
      className="animate-rise flex items-end justify-between gap-4 rounded-b-2xl border-t border-border bg-muted/40 px-6 py-4 sm:px-7"
      style={{ '--rise-delay': '1150ms' } as React.CSSProperties}
    >
      <div>
        <p className="text-sm font-medium text-foreground/80">Match today</p>
        <p className="text-sm text-foreground/55">with a clear path to raise it</p>
      </div>
      <p
        ref={matchRef as React.RefObject<HTMLParagraphElement>}
        className="text-5xl leading-none font-bold tracking-tight tabular-nums lining-nums"
      >
        {matchValue}
        <span className="text-foreground/40">%</span>
      </p>
    </div>
  </div>
);

/* ---------- Device: roster matches for a recruiter search ---------- */

const ROSTER_MATCHES: { name: string; coverage: string; score: number; tone: string }[] = [
  { name: 'D. Whitfield', coverage: '9 of 11 requirements met', score: 82, tone: 'text-success' },
  { name: 'M. Okafor', coverage: '7 of 11 requirements met', score: 64, tone: 'text-warning' },
  { name: 'R. Castellanos', coverage: '4 of 11 requirements met', score: 38, tone: 'text-foreground/50' },
];

/* ---------- Page ---------- */

export const LandingPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { onGetStarted } = useOutletContext<{ onGetStarted: () => void }>();
  const match = useCountUp(78, 1600);
  const delta = useCountUp(86);

  // Handle hash-based redirects (e.g., /#how-it-works → /how-it-works)
  useEffect(() => {
    if (location.hash === '#how-it-works') {
      navigate('/how-it-works');
    }
  }, [location.hash, navigate]);

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Career Catalyst - Know Where You Stand. Close the Gap.</title>
        <meta
          name="description"
          content="Career Catalyst measures your experience against the job you want, requirement by requirement, then guides you through closing the distance: your gaps, your story, your resume."
        />
        <link rel="canonical" href="https://careercatalyst.cc/" />
      </Helmet>

      {/* ---------- Hero ---------- */}
      <section className="relative overflow-hidden">
        {/* Brand aurora in the wordmark's family */}
        <div aria-hidden className="pointer-events-none absolute inset-0">
          <div className="animate-aurora-a absolute -top-[35%] left-[0%] h-[44rem] w-[44rem] rounded-full bg-brand-from opacity-35 blur-[130px]" />
          <div className="animate-aurora-b absolute -top-[12%] right-[-10%] h-[38rem] w-[38rem] rounded-full bg-brand-via opacity-30 blur-[130px]" />
          <div className="absolute top-[35%] left-[40%] h-[24rem] w-[24rem] rounded-full bg-brand-to opacity-15 blur-[140px]" />
          <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-b from-transparent to-background" />
        </div>

        <div className="relative mx-auto max-w-6xl px-4 pt-14 pb-16 sm:px-6 sm:pt-16 sm:pb-20">
          <h1 className="animate-rise text-[2.6rem] leading-[1.04] font-bold tracking-tight sm:text-6xl lg:text-7xl">
            Know where you&nbsp;stand.
            <br />
            <span className={BRAND_GRADIENT_TEXT}>Close the&nbsp;gap.</span>
          </h1>

          <div className="mt-8 grid grid-cols-1 items-start gap-10 lg:mt-10 lg:grid-cols-[1fr_1.1fr] lg:gap-14">
            {/* Left: copy + CTAs */}
            <div>
              <p
                className="animate-rise max-w-xl text-lg leading-relaxed text-foreground/80 sm:text-xl"
                style={{ '--rise-delay': '150ms' } as React.CSSProperties}
              >
                Career Catalyst measures your experience against the job you want, requirement by
                requirement. Then it guides you through the work of closing the distance: your
                gaps, your story, your resume.
              </p>
              <div
                className="animate-rise mt-8 flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4"
                style={{ '--rise-delay': '250ms' } as React.CSSProperties}
              >
                <button type="button" onClick={() => onGetStarted()} className={CTA_PRIMARY}>
                  Get started free
                </button>
                <Link to="/how-it-works" className={CTA_SECONDARY}>
                  How it works
                </Link>
              </div>
              <p
                className="animate-rise mt-4 text-sm text-foreground/55"
                style={{ '--rise-delay': '330ms' } as React.CSSProperties}
              >
                3 free tailoring credits when you sign up. No card required.
              </p>

              {/* Proof strip */}
              <div
                className="animate-rise mt-8 grid grid-cols-1 gap-4 border-t border-border pt-6 sm:grid-cols-3"
                style={{ '--rise-delay': '420ms' } as React.CSSProperties}
              >
                {[
                  ['Requirement-level', 'gap analysis on every posting'],
                  ['Source-traced', 'resumes built from your portfolio'],
                  ['ATS + recruiter', 'checks on every resume'],
                ].map(([strong, rest]) => (
                  <p key={strong} className="text-sm leading-snug text-foreground/60">
                    <span className="font-semibold text-foreground">{strong}</span> {rest}
                  </p>
                ))}
              </div>
            </div>

            {/* Right: the product story on first paint */}
            <ReviewedPosting matchValue={match.value} matchRef={match.ref} />
          </div>
        </div>
      </section>

      {/* ---------- From documents to dream job ---------- */}
      <section className="border-t border-border">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20">
          <Reveal>
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
              From documents to dream job.
            </h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 gap-10 md:grid-cols-3 md:gap-8">
            {[
              [
                '01',
                'Upload your documents',
                'Resumes, project docs, reviews. Career Catalyst extracts every experience, action, and skill into a structured portfolio.',
              ],
              [
                '02',
                'Target a job',
                "Paste a posting. Get a requirement-by-requirement read of where you match, where you're close, and where you're not.",
              ],
              [
                '03',
                'Do the work, then apply',
                'Close what gaps you can, generate a resume you can defend, and send your advocate along with it.',
              ],
            ].map(([num, title, body], i) => (
              <Reveal key={num} delay={i * 120}>
                <p className={cn('text-3xl font-bold tabular-nums', BRAND_GRADIENT_TEXT)}>{num}</p>
                <h3 className="mt-3 text-lg font-semibold">{title}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-foreground/65">{body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- Close the gaps ---------- */}
      <section className="border-t border-border bg-card/30">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20">
          <Reveal>
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
              Work the gaps, one conversation at a time.
            </h2>
            <p className="mt-3 max-w-2xl text-lg leading-relaxed text-foreground/70">
              Every gap gets a working session with a guide that holds your experience to a hiring
              manager's standard. Real evidence in, match score up.
            </p>
          </Reveal>

          <Reveal delay={120}>
            <div className="mt-10 grid grid-cols-1 items-stretch gap-6 lg:grid-cols-[1.5fr_1fr]">
              <div className="space-y-6 rounded-2xl border border-border bg-card/80 p-6 sm:p-7">
                <div className="border-l-2 border-primary/60 pl-5">
                  <p className="text-[11px] font-semibold tracking-[0.14em] text-primary uppercase">
                    Gap Navigator
                  </p>
                  <p className="mt-1.5 text-lg leading-relaxed text-foreground">
                    “You mentioned reducing infrastructure spend. What was the result,
                    specifically?”
                  </p>
                </div>
                <div className="border-l-2 border-border pl-5">
                  <p className="text-[11px] font-semibold tracking-[0.14em] text-foreground/60 uppercase">
                    You
                  </p>
                  <p className="mt-1.5 text-lg leading-relaxed text-foreground">
                    “We cut compute cost 31% over two quarters by consolidating workloads.”
                  </p>
                </div>
                <p className="text-sm text-foreground/60">
                  Cloud cost optimization ·{' '}
                  <span className="font-medium text-warning">Partial</span> →{' '}
                  <span className="font-medium text-success">Matched</span>
                </p>
              </div>

              <div className="flex flex-col justify-center rounded-2xl border border-border bg-card/80 p-6 text-center sm:p-7">
                <p
                  ref={delta.ref as React.RefObject<HTMLParagraphElement>}
                  className="text-6xl leading-none font-bold tracking-tight tabular-nums lining-nums"
                >
                  <span className="text-foreground/35">78</span>
                  <span className="mx-2 text-foreground/30">→</span>
                  <span className="text-success">{delta.value}</span>
                  <span className="text-foreground/40">%</span>
                </p>
                <p className="mt-3 text-base font-medium text-foreground/80">
                  one conversation later
                </p>
                <p className="mt-1 text-sm text-foreground/55">
                  Evidence you already had, told properly.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ---------- Defend + Scan, two-up ---------- */}
      <section className="border-t border-border">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 lg:gap-10">
            <Reveal>
              <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
                A resume you can defend.
              </h2>
              <p className="mt-3 max-w-lg text-lg leading-relaxed text-foreground/70">
                Every line is written from your portfolio and traces back to the document it came
                from. Nothing you can't stand behind in the interview.
              </p>
              <div className="mt-8 rounded-2xl border border-border bg-card/80 p-6 sm:p-7">
                <p className="text-lg leading-relaxed text-foreground">
                  Reduced cloud compute spend 31% in two quarters by consolidating workloads
                  across three product teams.
                  <sup className="ml-1 text-sm font-semibold text-primary">1</sup>
                </p>
                <div className="mt-6 border-t border-border pt-4">
                  <p className="text-sm text-foreground/60">
                    <span className="font-semibold text-primary">1</span>&nbsp;&nbsp;Source:
                    Meridian infrastructure review, uploaded March 2024
                  </p>
                </div>
              </div>
            </Reveal>

            <Reveal delay={120}>
              <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">Pass the scan.</h2>
              <p className="mt-3 max-w-lg text-lg leading-relaxed text-foreground/70">
                Before a person reads your resume, software does. Then a recruiter gives it six
                seconds. Every resume is checked against both.
              </p>
              <div className="mt-8 rounded-2xl border border-border bg-card/80 px-6 sm:px-7">
                {[
                  ['Keyword coverage', 'Strong'],
                  ['Format parseability', 'Clean'],
                  ['Recruiter six-second read', 'Interview'],
                ].map(([label, value], i) => (
                  <div
                    key={label}
                    className={cn(
                      'flex items-center justify-between py-[1.15rem]',
                      i > 0 && 'border-t border-border',
                    )}
                  >
                    <p className="text-base text-foreground sm:text-lg">{label}</p>
                    <p className="flex items-center gap-2 font-mono text-sm font-medium tracking-[0.1em] text-success uppercase">
                      <span className="size-1.5 rounded-full bg-success" />
                      {value}
                    </p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ---------- Advocate ---------- */}
      <section className="border-t border-border bg-card/30">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20">
          <Reveal>
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
              Send an advocate with your resume.
            </h2>
            <p className="mt-3 max-w-2xl text-lg leading-relaxed text-foreground/70">
              Share a private link with any employer. After a quick email verification, they can
              ask your Personal Advocate anything about your background. It answers from your
              portfolio, with receipts, and you see every view and question.
            </p>
          </Reveal>

          <Reveal delay={120}>
            <div className="mt-10 grid grid-cols-1 items-stretch gap-6 lg:grid-cols-[1.5fr_1fr]">
              <div className="space-y-6 rounded-2xl border border-border bg-card/80 p-6 sm:p-7">
                <div className="border-l-2 border-border pl-5">
                  <p className="text-[11px] font-semibold tracking-[0.14em] text-foreground/60 uppercase">
                    Employer · verified
                  </p>
                  <p className="mt-1.5 text-lg leading-relaxed text-foreground">
                    “How do they handle projects where the requirements keep moving?”
                  </p>
                </div>
                <div className="border-l-2 border-primary/60 pl-5">
                  <p className="text-[11px] font-semibold tracking-[0.14em] text-primary uppercase">
                    Personal Advocate
                  </p>
                  <p className="mt-1.5 text-lg leading-relaxed text-foreground">
                    “At Meridian Systems they scoped a workload migration while requirements were
                    still shifting, and delivered it in two quarters. I can point you to the
                    specifics in their portfolio.”
                  </p>
                </div>
              </div>

              <div className="flex flex-col justify-center gap-5 rounded-2xl border border-border bg-card/80 p-6 sm:p-7">
                {[
                  ['127', 'profile views'],
                  ['23', 'questions answered'],
                  ['100%', 'sourced from your portfolio'],
                ].map(([value, label]) => (
                  <div key={label} className="flex items-baseline justify-between gap-4">
                    <p className="text-3xl font-bold tracking-tight tabular-nums lining-nums">
                      {value}
                    </p>
                    <p className="text-right text-sm text-foreground/60">{label}</p>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ---------- Portfolio compounds ---------- */}
      <section className="border-t border-border">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20">
          <Reveal>
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
              Your portfolio compounds.
            </h2>
            <p className="mt-3 max-w-2xl text-lg leading-relaxed text-foreground/70">
              Every resolved gap writes new evidence back. Every tailoring draws from the same
              bank. The work you do for one job strengthens all of them.
            </p>
          </Reveal>

          <Reveal delay={120}>
            <div className="mt-10 rounded-2xl border border-border bg-card/80 p-6 sm:p-7">
              <div className="flex flex-wrap items-baseline justify-between gap-3">
                <div>
                  <h3 className="text-lg font-semibold">Platform Engineer · Meridian Systems</h3>
                  <p className="text-sm text-foreground/55">
                    2021 to 2024 · extracted from your documents
                  </p>
                </div>
                <p className="text-sm text-foreground/55">4 skills evidenced</p>
              </div>
              <div className="mt-5 space-y-4 border-t border-border pt-5">
                <p className="text-base leading-relaxed text-foreground">
                  Consolidated workloads across three product teams, cutting compute cost 31% in
                  two quarters.
                  <span className="ml-2 text-xs font-semibold tracking-[0.12em] text-success uppercase">
                    added from gap session
                  </span>
                </p>
                <p className="text-base leading-relaxed text-foreground/85">
                  Led migration scoping with stakeholders across engineering and finance.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ---------- Research line ---------- */}
      <section className="border-t border-border">
        <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6">
          <Reveal>
            <p className="text-base text-foreground/60">
              The AI behind Career Catalyst was chosen through our own published benchmark of 46
              models.{' '}
              <Link
                to="/research/benchmarking-46-llms"
                className="font-medium text-primary hover:underline"
              >
                Read the research →
              </Link>
            </p>
          </Reveal>
        </div>
      </section>

      {/* ---------- For recruiters ---------- */}
      <section className="border-t border-border">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20">
          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-[1.1fr_1fr] lg:gap-14">
            <Reveal>
              <p className="text-[11px] font-semibold tracking-[0.14em] text-primary uppercase">
                For recruiting teams and agencies
              </p>
              <h2 className="mt-3 text-3xl font-bold tracking-tight sm:text-4xl">
                The same clarity, across your whole roster.
              </h2>
              <p className="mt-3 max-w-xl text-lg leading-relaxed text-foreground/70">
                Run every search against your candidate roster and see exactly why each candidate
                matches, requirement by requirement.
              </p>
              <div className="mt-6 space-y-3">
                {[
                  [
                    'Transparent matching',
                    'shows the requirement coverage behind every score.',
                  ],
                  [
                    'A working pipeline',
                    'moves candidates through stages and records real hiring outcomes.',
                  ],
                  [
                    'Bulk import',
                    'brings your placement history over in one package.',
                  ],
                ].map(([strong, rest]) => (
                  <p key={strong} className="text-[15px] leading-snug text-foreground/60">
                    <span className="font-semibold text-foreground">{strong}</span> {rest}
                  </p>
                ))}
              </div>
              <div className="mt-8">
                <Link to="/signup?type=recruiter" className={CTA_SECONDARY}>
                  Create your agency workspace
                </Link>
              </div>
            </Reveal>

            <Reveal delay={120}>
              <div
                aria-hidden
                className="w-full rounded-2xl border border-border bg-card/80 shadow-xl backdrop-blur-xs"
              >
                <div className="px-6 pt-5 sm:px-7">
                  <p className="text-[11px] font-semibold tracking-[0.14em] text-muted-foreground uppercase">
                    Senior Claims Adjuster · roster matches
                  </p>
                </div>
                <div className="mt-3 divide-y divide-border border-t border-border px-6 sm:px-7">
                  {ROSTER_MATCHES.map((row) => (
                    <div key={row.name} className="flex items-center justify-between gap-5 py-3.5">
                      <div>
                        <p className="text-[15px] font-medium text-foreground sm:text-base">
                          {row.name}
                        </p>
                        <p className="text-sm text-foreground/55">{row.coverage}</p>
                      </div>
                      <p
                        className={cn(
                          'text-2xl font-bold tracking-tight tabular-nums lining-nums',
                          row.tone,
                        )}
                      >
                        {row.score}
                        <span className="text-foreground/40">%</span>
                      </p>
                    </div>
                  ))}
                </div>
                <div className="rounded-b-2xl border-t border-border bg-muted/40 px-6 py-4 sm:px-7">
                  <p className="text-sm text-foreground/60">
                    Every score opens into the requirements behind it.
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ---------- Final CTA ---------- */}
      <section className="border-t border-border">
        <div className="mx-auto max-w-6xl px-4 py-20 text-center sm:px-6 sm:py-24">
          <Reveal>
            <h2 className="text-4xl font-bold tracking-tight sm:text-5xl">
              Know where you stand.
              <br />
              <span className={BRAND_GRADIENT_TEXT}>Close the gap.</span>
            </h2>
            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row sm:gap-4">
              <button
                type="button"
                onClick={() => onGetStarted()}
                className={cn(CTA_PRIMARY, 'px-8')}
              >
                Get started free
              </button>
            </div>
            <p className="mt-4 text-sm text-foreground/55">
              3 free tailoring credits. No card required.
            </p>
          </Reveal>
        </div>
      </section>
    </div>
  );
};
